import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Button,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Grid,
  Paper
} from '@mui/material';
import {
  PlayArrow as RunningIcon,
  Schedule as QueueIcon,
  CheckCircle as CompletedIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Replay as RetryIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Stop as StopIcon
} from '@mui/icons-material';

const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

// FIXED: Proper status determination functions
const isCompleted = (job) => {
  // A job is completed ONLY if status is 'completed' - don't rely on completed_at alone
  if (!job) return false;
  return job.status === 'completed';
};

const isFailed = (job) => {
  // A job is failed if status is 'failed'
  if (!job) return false;
  return job.status === 'failed';
};

const isCancelled = (job) => {
  // A job is cancelled if status is 'cancelled'
  if (!job) return false;
  return job.status === 'cancelled';
};

const isQueued = (job) => {
  // A job is queued if it has status 'running' but no worker_id (waiting in queue)
  if (!job) return false;
  return job.status === 'running' && !job.worker_id && !isCompleted(job) && !isFailed(job);
};

const isActuallyRunning = (job) => {
  // A job is actually running if it has status 'running' AND has a worker_id
  if (!job) return false;
  return job.status === 'running' && !!job.worker_id && !isCompleted(job) && !isFailed(job);
};

// FIXED: Get proper status for display
const getJobDisplayStatus = (job) => {
  if (isCompleted(job)) return 'completed';
  if (isFailed(job)) return 'failed';
  if (isCancelled(job)) return 'cancelled';
  if (isActuallyRunning(job)) return 'running';
  if (isQueued(job)) return 'queued';
  return job.status || 'unknown';
};

const QueueStatusPanel = ({ 
  queueStatus = { running: [], queued: [], recent: [], completedToday: 0 }, 
  onJobAction,
  onRefresh,
  loading = false,
  onActivityFilter = null, // Callback to filter Recent Activity by job status
  activityFilter = '' // Currently selected activity filter (e.g., 'failed', 'completed')
}) => {
  const [showAllRecent, setShowAllRecent] = React.useState(false);
  
  const formatDuration = (minutes) => {
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    return `${hours}h ${remainingMinutes}m`;
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    const now = new Date();
    let time;
    
    // Handle different timestamp formats
    if (typeof timestamp === 'string') {
      // Handle ISO string format or ClickHouse format
      time = new Date(timestamp.replace(' ', 'T') + (timestamp.includes('T') ? '' : 'Z'));
    } else if (typeof timestamp === 'object' && timestamp instanceof Date) {
      time = timestamp;
    } else {
      console.warn('Invalid timestamp format:', timestamp);
      return 'Invalid time';
    }
    
    // Check if the date is valid
    if (isNaN(time.getTime())) {
      console.warn('Invalid date created from timestamp:', timestamp);
      return 'Invalid time';
    }
    
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const formatRetryTime = (logsPreview) => {
    if (!logsPreview || !logsPreview.startsWith('RETRY_AT:')) {
      return null;
    }
    
    try {
      const retryTimestamp = parseInt(logsPreview.split(':')[1]);
      const retryTime = new Date(retryTimestamp);
      const now = new Date();
      
      if (retryTime <= now) {
        return 'Ready to retry';
      }
      
      const diffMs = retryTime - now;
      const diffMins = Math.ceil(diffMs / (1000 * 60));
      
      if (diffMins < 60) {
        return `Retry in ${diffMins}m`;
      }
      
      const diffHours = Math.ceil(diffMins / 60);
      if (diffHours < 24) {
        return `Retry in ${diffHours}h`;
      }
      
      return `Retry at ${retryTime.toLocaleTimeString()}`;
    } catch (error) {
      console.warn('Error parsing retry time:', error);
      return 'Retry scheduled';
    }
  };

  const calculateJobDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    
    const start = new Date(startTime.replace ? startTime.replace(' ', 'T') + (startTime.includes('T') ? '' : 'Z') : startTime);
    const end = new Date(endTime.replace ? endTime.replace(' ', 'T') + (endTime.includes('T') ? '' : 'Z') : endTime);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    
    return (end - start) / (1000 * 60); // Duration in minutes
  };

  // FIXED: Proper status color mapping
  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'success';
      case 'queued': return 'warning';
      case 'completed': return 'success';
      case 'failed': return 'error';  // This was missing proper handling
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  // FIXED: Proper status icon mapping
  const getStatusIcon = (status) => {
    switch (status) {
      case 'running': return <RunningIcon />;
      case 'queued': return <QueueIcon />;
      case 'completed': return <CompletedIcon />;
      case 'failed': return <ErrorIcon />;  // Proper error icon
      case 'cancelled': return <CancelIcon />;
      default: return <InfoIcon />;
    }
  };

  // FIXED: Proper categorization of jobs from the queueStatus data
  const rawRunning = Array.isArray(queueStatus.running) ? queueStatus.running : [];
  const rawQueued = Array.isArray(queueStatus.queued) ? queueStatus.queued : [];
  const rawRecent = Array.isArray(queueStatus.recent) ? queueStatus.recent : [];

  // Filter based on actual job status, not on our frontend logic
  const runningJobs = rawRunning.filter(job => isActuallyRunning(job));
  const queuedJobs = rawQueued.filter(job => isQueued(job));
  
  // Recent jobs are already filtered by backend when activityFilter is set
  // No need for frontend filtering - just use what backend sends
  const recentJobs = rawRecent;

  // Helper to format connector display name with platform prefix
  const formatConnectorDisplayName = (job) => {
    // If we have a connector_name, use it with the platform display name
    if (job.connector_name) {
      const platformName = job.platform_display_name || job.platform_name || 'Platform';
      // Check if connector_name already starts with the platform name
      if (job.connector_name.toLowerCase().startsWith(platformName.toLowerCase())) {
        return job.connector_name; // Already has prefix
      }
      return `${platformName} - ${job.connector_name}`;
    }
    
    // Fallback to original logic
    if (job.display_name) {
      return job.display_name;
    }
    
    // Construct from platform and brand
    const platform = job.platform_display_name || job.platform_name || 'Unknown Platform';
    const brand = job.brand_name ? ` - ${job.brand_name}` : '';
    return `${platform}${brand}`;
  };

  const JobCard = ({ job, showActions = true, variant = 'outlined' }) => {
    const displayStatus = getJobDisplayStatus(job);
    const queued = displayStatus === 'queued';
    const actuallyRunning = displayStatus === 'running';
    const completed = displayStatus === 'completed';
    const failed = displayStatus === 'failed';
    const cancelled = displayStatus === 'cancelled';
    const retryInfo = formatRetryTime(job.logs_preview);

    // FIXED: Progress calculation - don't force to 100% unless actually completed
    let displayPct = clamp(Number(job.progress_percentage || 0), 0, 100);
    if (completed && displayPct < 100) displayPct = 100;
    if (failed || cancelled) displayPct = 0; // Reset progress for failed jobs
    
    return (
    <Paper 
      variant={variant} 
      sx={{ 
        p: 2, 
        mb: 1, 
        border: 1,
        borderColor: `${getStatusColor(displayStatus)}.main`,
        borderRadius: 1
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Job Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip 
              icon={getStatusIcon(displayStatus)}
              label={displayStatus.toUpperCase()}
              size="small"
              color={getStatusColor(displayStatus)}
              variant="outlined"
            />
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {formatConnectorDisplayName(job)}
            </Typography>
            {job.geographical_info && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {job.geographical_info}
              </Typography>
            )}
            <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }} noWrap>
              ID: {job.id}
            </Typography>
          </Box>

          {/* Job Details */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {job.date_range || 
            (job.date_range_start && job.date_range_end 
            ? `${job.date_range_start} to ${job.date_range_end}`
            : 'Date range not specified')
            }
            {job.priority && ` • Priority: ${job.priority}`}
              {retryInfo && queued && (
                <Box component="span" sx={{ 
                  ml: 1, 
                  px: 1, 
                  py: 0.5, 
                  bgcolor: 'warning.light', 
                  borderRadius: 1,
                  fontSize: '0.75rem'
                }}>
                  {retryInfo}
                </Box>
              )}
          </Typography>

          {/* FIXED: Progress for running jobs only */}
          {actuallyRunning && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {job.current_step || 'Processing...'}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {displayPct}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={displayPct}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          )}

          {/* FIXED: Timing Information with proper status handling */}
          <Typography variant="caption" color="text.secondary">
            {actuallyRunning && job.started_at && 
              `Started ${formatRelativeTime(job.started_at)}`
            }
            {queued && 
              `Queued ${formatRelativeTime(job.created_at)} • Est. ${job.estimated_duration_minutes || 30}min`
            }
            {completed && job.completed_at && job.started_at &&
              `Completed ${formatRelativeTime(job.completed_at)} in ${formatDuration(calculateJobDuration(job.started_at, job.completed_at))}`
            }
            {failed && job.completed_at &&
              `Failed ${formatRelativeTime(job.completed_at)}${job.error_message ? ` • ${job.error_message.substring(0, 50)}...` : ''}`
            }
            {cancelled && job.completed_at &&
              `Cancelled ${formatRelativeTime(job.completed_at)}`
            }
          </Typography>
        </Box>

        {/* Actions */}
        {showActions && (
          <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="View Details">
                <IconButton 
                  size="small" 
                  onClick={() => onJobAction('view', job)}
                >
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              {queued && (
                <>
                  <Tooltip title="Cancel Job">
                    <IconButton 
                      size="small" 
                      color="warning"
                      onClick={() => onJobAction('cancel', job)}
                    >
                      <StopIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Job">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => onJobAction('delete', job)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              
              {actuallyRunning && (
                <Tooltip title="Stop Running Job">
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => onJobAction('cancel', job)}
                  >
                    <StopIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              
              {(failed || cancelled) && (
                <Tooltip title="Retry Job">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => onJobAction('retry', job)}
                  >
                    <RetryIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
    );
  };

  return (
    <Card sx={{ mb: 3, position: 'relative' }}>
      <CardHeader 
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QueueIcon color="primary" />
            <Typography variant="h6">Data Sync Queue</Typography>
          </Box>
        }
        action={
          <Tooltip title="Refresh Status">
            <IconButton onClick={onRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />
      
      <CardContent>
        {/* FIXED: Queue Overview Stats - Now showing correct counts */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
              <Typography variant="h4" color="success.dark">
                {runningJobs.length || 0}
              </Typography>
              <Typography variant="body2" color="success.dark">
                Running
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
              <Typography variant="h4" color="warning.dark">
                {queuedJobs.length || 0}
              </Typography>
              <Typography variant="body2" color="warning.dark">
                In Queue
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
              <Typography variant="h4" color="primary.dark">
                {queueStatus.completedToday || 0}
              </Typography>
              <Typography variant="body2" color="primary.dark">
                Completed Today
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={3}>
            <Paper 
              sx={{ 
                p: 2, 
                textAlign: 'center', 
                bgcolor: activityFilter === 'failed' ? 'error.dark' : 'error.light',
                cursor: onActivityFilter ? 'pointer' : 'default',
                transition: 'all 0.2s',
                '&:hover': onActivityFilter ? {
                  bgcolor: 'error.dark',
                  transform: 'scale(1.02)',
                  boxShadow: 3
                } : {}
              }}
              onClick={() => {
                if (onActivityFilter) {
                  // Toggle filter: if already filtered by failed, clear it; otherwise set to failed
                  onActivityFilter(activityFilter === 'failed' ? '' : 'failed');
                }
              }}
            >
              <Typography 
                variant="h4" 
                color={activityFilter === 'failed' ? 'error.contrastText' : 'error.dark'}
              >
                {queueStatus.failedToday || 0}
              </Typography>
              <Typography 
                variant="body2" 
                color={activityFilter === 'failed' ? 'error.contrastText' : 'error.dark'}
              >
                Failed Today
              </Typography>
              {onActivityFilter && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    mt: 0.5,
                    color: activityFilter === 'failed' ? 'error.contrastText' : 'error.dark',
                    opacity: 0.8
                  }}
                >
                  {activityFilter === 'failed' ? '✓ Click to clear' : 'Click to filter'}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Job sections container with fixed minimum height to prevent layout shift */}
        <Box sx={{ minHeight: '200px' }}>
          {/* Running Jobs */}
          {runningJobs.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RunningIcon color="success" />
                Currently Running ({runningJobs.length})
              </Typography>
              {runningJobs.map(job => (
                <JobCard key={job.id} job={job} />
              ))}
            </Box>
          )}

          {/* Queued Jobs */}
          {queuedJobs.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QueueIcon color="warning" />
                In Queue ({queuedJobs.length})
              </Typography>
              {queuedJobs.slice(0, 5).map((job, index) => (
                <JobCard key={job.id} job={{...job, position: index + 1}} />
              ))}
              {queuedJobs.length > 5 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  ... and {queuedJobs.length - 5} more jobs in queue
                </Alert>
              )}
            </Box>
          )}

          {/* Recent Activity - FIXED: Now shows proper status colors with View All option */}
          {recentJobs.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CompletedIcon color="primary" />
                    Recent Activity
                  </Typography>
                  {activityFilter && (
                    <Chip
                      label={`Showing: ${activityFilter}`}
                      size="small"
                      color={activityFilter === 'failed' ? 'error' : 'default'}
                      onDelete={() => onActivityFilter && onActivityFilter('')}
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
                {recentJobs.length > 3 && (
                  <Button
                    size="small"
                    onClick={() => setShowAllRecent(!showAllRecent)}
                    sx={{ textTransform: 'none' }}
                  >
                    {showAllRecent ? 'Show Less' : `View All (${recentJobs.length})`}
                  </Button>
                )}
              </Box>
              {(showAllRecent ? recentJobs : recentJobs.slice(0, 3)).map(job => (
                <JobCard key={job.id} job={job} variant="elevation" />
              ))}
            </Box>
          )}

          {/* Empty State */}
          {(runningJobs.length === 0) && (queuedJobs.length === 0) && (recentJobs.length === 0) && (
            <Alert severity="info" sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                {activityFilter ? `No ${activityFilter} jobs found` : 'No Recent Queue Activity'}
              </Typography>
              <Typography variant="body2">
                {activityFilter 
                  ? `There are no jobs with status "${activityFilter}" in recent activity. Clear the filter to see all jobs.`
                  : 'Sync jobs will appear here when you trigger data synchronization.'}
              </Typography>
              {activityFilter && onActivityFilter && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onActivityFilter('')}
                  sx={{ mt: 2 }}
                >
                  Clear Filter
                </Button>
              )}
            </Alert>
          )}
        </Box>

        {/* Loading overlay - positioned absolutely to not cause layout shift */}
        {loading && (
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            py: 1
          }}>
            <LinearProgress sx={{ mb: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
              Updating...
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default QueueStatusPanel;